import { StorageAreaStatus } from 'shared/storageAreas/domain/storageAreaStatus';
import {
  isBulkStorage,
  isCoilStorage,
  isLogYardPile,
  StorageAreaType,
} from 'shared/storageAreas/domain/storageAreaType';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useSelectedLogYardStore } from './selectedLogYardStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useSelectedCoilStackStore } from './selectedCoilStackStore';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';
import { useSelectedBulkStore } from './selectedBulkStore';

export interface CreateStorageArea {
  type: StorageAreaType;
  name: string;
  allowedTypes: StorageAreaType[];
}

export interface EditStorageArea {
  storageArea: StorageArea;
  multiple: boolean;
}

export interface CopyStorageArea {
  storageArea: StorageArea;
}

export interface ProductOption {
  name: string;
  value: number;
}

export interface TypeOption {
  name: string;
  value: StorageAreaType;
}

export const useSelectedStorageAreaStore = defineStore('selectedStorageArea', () => {
  const logYardStore = useSelectedLogYardStore();
  const coilStackStore = useSelectedCoilStackStore();
  const bulkStorageStore = useSelectedBulkStore();
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);
  const featureFlagStore = useFeatureFlagStore();
  const storageAreaStore = useStorageAreaStore();

  const operation = ref<'create' | 'edit' | 'none'>('none');
  const currentStorageArea = ref<StorageArea>();
  const selectedStorageAreaIds = ref<string[]>([]);
  const editMultiple = computed(() => selectedStorageAreaIds.value.length > 1);

  const name = ref<string>('');
  const nameInitial = computed(() => currentStorageArea.value?.name);
  const nameChanged = computed(
    () => operation.value === 'edit' && name.value !== nameInitial.value,
  );
  const nameError = computed(() => {
    if (name.value === '') {
      return translations.value.error.nameIsRequired;
    }

    return undefined;
  });
  const nameEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstName = storageAreas[0]!.name;
    return storageAreas.every((sa) => sa!.name === firstName);
  });

  const storageAreaId = computed(() => currentStorageArea.value?.id);

  const active = ref<boolean>(true);
  const activeInitial = computed(() => currentStorageArea.value?.active ?? true);
  const activeChanged = computed(
    () => operation.value === 'edit' && active.value !== activeInitial.value,
  );
  const activeEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstActive = storageAreas[0]!.active;
    return storageAreas.every((sa) => sa!.active === firstActive);
  });
  const temporary = ref<boolean>(false);
  const version = ref<string>();

  const type = ref<StorageAreaType>(StorageAreaType.Undefined);
  const typeInitial = computed(() => currentStorageArea.value?.type);
  const typeChanged = computed(
    () => operation.value === 'edit' && type.value !== typeInitial.value,
  );
  const typeEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstType = storageAreas[0]!.type;
    return storageAreas.every((sa) => sa!.type === firstType);
  });

  const typeOptions = ref<TypeOption[]>([]);

  const status = ref<StorageAreaStatus>(StorageAreaStatus.Undefined);
  const statusInitial = computed(() => currentStorageArea.value?.status);
  const statusChanged = computed(
    () => operation.value === 'edit' && status.value !== statusInitial.value,
  );
  const statusEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstStatus = storageAreas[0]!.status;
    return storageAreas.every((sa) => sa!.status === firstStatus);
  });

  const note = ref<string>('');
  const noteInitial = computed(() => currentStorageArea.value?.note ?? '');
  const noteChanged = computed(
    () => operation.value === 'edit' && note.value !== noteInitial.value,
  );
  const noteEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstNote = storageAreas[0]!.note ?? '';
    return storageAreas.every((sa) => (sa!.note ?? '') === firstNote);
  });

  const importantNote = ref(false);
  const importantNoteInitial = computed(() => currentStorageArea.value?.importantNote ?? false);
  const importantNoteChanged = computed(
    () => operation.value === 'edit' && importantNote.value !== importantNoteInitial.value,
  );
  const importantNoteEnabled = computed<boolean>(() => {
    if (!editMultiple.value) return true;

    const storageAreas = selectedStorageAreaIds.value
      .map((id) => storageAreaStore.getById(id))
      .filter((s) => s !== undefined);
    if (storageAreas.length === 0) return false;

    const firstImportantNote = storageAreas[0]!.importantNote ?? false;
    return storageAreas.every((sa) => (sa!.importantNote ?? false) === firstImportantNote);
  });

  const storageAreaChanged = computed(() => {
    if (operation.value === 'create') return true;
    if (operation.value === 'none') return false;

    return (
      nameChanged.value ||
      typeChanged.value ||
      statusChanged.value ||
      logYardStore.logYardChanged ||
      coilStackStore.changed ||
      bulkStorageStore.bulkStorageChanged ||
      noteChanged.value ||
      activeChanged.value ||
      importantNoteChanged.value
    );
  });
  const storageAreaInvalid = computed(
    () => operation.value !== 'none' && nameError.value !== undefined && !logYardStore.invalid,
  );

  storageAreaStore.changed.on((ev) => {
    if (currentStorageArea.value?.id !== ev.id) return;
    if (ev.action === 'removed') {
      hide();
      return;
    }

    const storageArea = ev.data;

    const changes = {
      name: nameChanged.value,
      type: typeChanged.value,
      status: statusChanged.value,
      note: noteChanged.value,
      importantNote: importantNoteChanged.value,
    };

    setCurrentStorageArea(storageArea);

    if (!changes.name) {
      name.value = storageArea.name;
    }

    if (!changes.type) {
      type.value = storageArea.type;
    }

    if (!changes.status) {
      status.value = storageArea.status;
    }

    if (!changes.note) {
      note.value = storageArea.note ?? '';
    }

    if (!changes.importantNote) {
      importantNote.value = storageArea.importantNote ?? false;
    }
  });

  function setCurrentStorageArea(storageArea: StorageArea) {
    currentStorageArea.value = JSON.parse(JSON.stringify(storageArea));
  }

  function create(action: CreateStorageArea) {
    operation.value = 'create';
    currentStorageArea.value = undefined;
    selectedStorageAreaIds.value = [];

    const allowedTypes = new Set<StorageAreaType>();
    for (const type of action.allowedTypes) {
      allowedTypes.add(type);
    }

    const types = new Set<StorageAreaType>();

    const addType = (type: StorageAreaType) => {
      if (!allowedTypes.has(type)) return;
      types.add(type);
    };

    if (featureFlagStore.isCoilEnabled()) {
      addType(StorageAreaType.CoilStack);
    }

    console.log("IsbulkEnablked", featureFlagStore.isBulkEnabled())

    if(featureFlagStore.isBulkEnabled()) {
      addType(StorageAreaType.Bulk)
    }

    if (featureFlagStore.isLogYardEnabled()) {
      addType(StorageAreaType.SortedPile);
      addType(StorageAreaType.UnsortedPile);
      addType(StorageAreaType.LogSorterIntake);
      addType(StorageAreaType.LogSorterBin);
      addType(StorageAreaType.SawLineIntake);
    }

    typeOptions.value = toTypeOptions(types);

    name.value = action.name;

    console.log("typeOptions", typeOptions.value)

    if (types.has(action.type)) {
      type.value = action.type;
    } else if (typeOptions.value.length === 1) {
      type.value = typeOptions.value[0]!.value;
    } else {
      type.value = StorageAreaType.Undefined;
    }

    status.value = StorageAreaStatus.Empty;
    active.value = true;
    temporary.value = false;
    version.value = undefined;
    note.value = '';
    importantNote.value = false;

    logYardStore.create();
    coilStackStore.create();
    bulkStorageStore.create();
  }

  function edit(action: EditStorageArea) {
    const { storageArea, multiple } = action;
    operation.value = 'edit';

    if (multiple) {
      const isFirstSelection = selectedStorageAreaIds.value.length === 0;

      // Add to selection of multiple storage areas
      selectedStorageAreaIds.value = [...selectedStorageAreaIds.value, storageArea.id];

      // If this is the first selection in multi-edit mode, treat it as a single edit
      if (isFirstSelection) {
        setCurrentStorageArea(storageArea);
        fill(storageArea);
        version.value = storageArea.version;
      } else if (editMultiple.value) {
        // We have multiple storage areas selected, check which fields match
        const storageAreas = selectedStorageAreaIds.value
          .map((id) => storageAreaStore.getById(id))
          .filter((s) => s !== undefined);

        // Update name if all have the same name
        if (nameEnabled.value) {
          const firstName = storageAreas[0]!.name;
          if (storageAreas.every((sa) => sa!.name === firstName)) {
            name.value = firstName;
          }
        }

        // Update type if all have the same type
        if (typeEnabled.value) {
          const firstType = storageAreas[0]!.type;
          if (storageAreas.every((sa) => sa!.type === firstType)) {
            type.value = firstType;
          }
        }

        // Update status if all have the same status
        if (statusEnabled.value) {
          const firstStatus = storageAreas[0]!.status;
          if (storageAreas.every((sa) => sa!.status === firstStatus)) {
            status.value = firstStatus;
          }
        }

        // Update active if all have the same active
        if (activeEnabled.value) {
          const firstActive = storageAreas[0]!.active;
          if (storageAreas.every((sa) => sa!.active === firstActive)) {
            active.value = firstActive;
          }
        }

        // Update note if all have the same note
        if (noteEnabled.value) {
          const firstNote = storageAreas[0]!.note ?? '';
          if (storageAreas.every((sa) => (sa!.note ?? '') === firstNote)) {
            note.value = firstNote;
          }
        }

        // Update importantNote if all have the same importantNote
        if (importantNoteEnabled.value) {
          const firstImportantNote = storageAreas[0]!.importantNote ?? false;
          if (storageAreas.every((sa) => (sa!.importantNote ?? false) === firstImportantNote)) {
            importantNote.value = firstImportantNote;
          }
        }
      }
    } else {
      // Single selection mode
      setCurrentStorageArea(storageArea);
      selectedStorageAreaIds.value = [storageArea.id];
      fill(storageArea);
      version.value = storageArea.version;
    }

    const logYard = storageArea.logYardData;

    if (logYard) {
      // Only pass storage area IDs that have logYard data
      const storageAreasWithLogYard = selectedStorageAreaIds.value.filter((id) => {
        const sa = storageAreaStore.getById(id);
        return sa?.logYardData !== null && sa?.logYardData !== undefined;
      });

      logYardStore.edit({
        logYard: logYard,
        storageAreaId: storageArea.id,
        selectedStorageAreaIds: storageAreasWithLogYard,
      });
    } else {
      logYardStore.create();
    }

    const bulkStorage = storageArea.bulkStorageData;

    if (bulkStorage) {
      // Only pass storage area IDs that have logYard data
      const storageAreasWithBulk = selectedStorageAreaIds.value.filter((id) => {
        const sa = storageAreaStore.getById(id);
        return sa?.bulkStorageData !== null && sa?.bulkStorageData !== undefined;
      });

      bulkStorageStore.edit({
        bulkData: bulkStorage,
        storageAreaId: storageArea.id,
        selectedStorageAreaIds: storageAreasWithBulk,
      });
    } else {
      bulkStorageStore.create();
    }

    const coilStack = storageArea.coilStack;

    if (coilStack) {
      coilStackStore.edit({
        coilStack,
        storageAreaId: storageArea.id,
      });
    } else {
      coilStackStore.create();
    }
  }

  function copy(action: CopyStorageArea) {
    operation.value = 'create';
    currentStorageArea.value = undefined;
    selectedStorageAreaIds.value = [];

    const storageArea = action.storageArea;

    fill(storageArea);
    name.value = storageAreaStore.generateDefaultName();
    version.value = undefined;

    if (isLogYardPile(storageArea.type)) {
      const logYard = storageArea.logYardData;
      if (logYard) {
        logYardStore.copy({
          logYard: logYard,
        });
      } else {
        logYardStore.create();
      }
    }

    if (isBulkStorage(storageArea.type)) {
      const bulkStorage = storageArea.bulkStorageData;
      if (bulkStorage) {
        bulkStorageStore.copy({
          bulkStorage: bulkStorage,
        });
      } else {
        bulkStorageStore.create();
      }
    }

    if (isCoilStorage(storageArea.type)) {
      const coilStack = storageArea.coilStack;
      if (coilStack) {
        coilStackStore.copy({
          coilStack,
        });
      } else {
        coilStackStore.create();
      }
    }
  }

  function fill(storageArea: StorageArea) {
    const types = new Set<StorageAreaType>();

    if (isCoilStorage(storageArea.type)) {
      types.add(StorageAreaType.CoilStack);
    }

    if (isLogYardPile(storageArea.type)) {
      types.add(StorageAreaType.SortedPile);
      types.add(StorageAreaType.UnsortedPile);
    }

    if (isBulkStorage(storageArea.type)) {
      types.add(StorageAreaType.Bulk);
    }

    types.add(storageArea.type);
    typeOptions.value = toTypeOptions(types);

    name.value = storageArea.name;
    type.value = storageArea.type;
    status.value = storageArea.status;
    active.value = storageArea.active;
    temporary.value = storageArea.temporary;
    note.value = storageArea.note ?? '';
    importantNote.value = storageArea.importantNote ?? false;
  }

  function hide() {
    operation.value = 'none';
    currentStorageArea.value = undefined;
    selectedStorageAreaIds.value = [];

    name.value = '';
    type.value = StorageAreaType.UnsortedPile;
    status.value = StorageAreaStatus.Empty;
    active.value = true;
    temporary.value = false;
    version.value = undefined;
    typeOptions.value = [];
    note.value = '';
    importantNote.value = false;

    logYardStore.hide();
    coilStackStore.hide();
    bulkStorageStore.hide();
  }

  function toTypeOptions(types: Set<StorageAreaType>) {
    const options = Array.from(types).map((t) => {
      return {
        name: translations.value.enums.storageAreaType(t),
        value: t,
      };
    });

    options.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    return options;
  }

  return {
    create,
    edit,
    copy,
    hide,
    storageAreaId,
    selectedStorageAreaIds,
    editMultiple,
    name,
    nameChanged,
    nameError,
    nameEnabled,
    type,
    typeChanged,
    typeEnabled,
    status,
    statusChanged,
    statusEnabled,
    storageAreaChanged,
    active,
    activeChanged,
    activeEnabled,
    temporary,
    version,
    storageAreaInvalid,
    operation,
    currentStorageArea,
    typeOptions,
    note,
    noteChanged,
    noteEnabled,
    importantNote,
    importantNoteChanged,
    importantNoteEnabled,
  };
});
